import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { forkJoin, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { JsonMapperService } from 'src/app/core/services/mapper/mapper.service';
import { Cahier } from '../../models/cahier.model';
import { ActivitesService } from '../activites/activites.service';
import { Cache, ClearCache } from 'src/app/core/services/cache/cache.service';

@Injectable({
    providedIn: 'root'
})
export class CahiersService {
    static cahiers: Cahier[];

    protected BASE_URL = '/api/habilitation';

    constructor(public readonly http: HttpClient, private readonly mapper: JsonMapperService, private readonly activitesService: ActivitesService) {}

    static getCahiers(): Cahier[] {
        const cahiers = CahiersService.cahiers;
        if (cahiers === undefined) {
            throw new Error('service-cahiers.static-data.unavailable');
        }
        return cahiers;
    }

    @ClearCache
    clearCache() {}

    @Cache()
    public getCahiers(): Observable<Cahier[]> {
        return forkJoin([
            this.http.get<object[]>(`${this.BASE_URL}/private/cahiers`),
            this.activitesService.getActivites() // activites must be loaded before deserialisation
        ]).pipe(
            map(([cahiers, activites]) => this.mapper.deserializeArray(cahiers, Cahier, { activites })),
            tap(cahiers => {
                CahiersService.cahiers = cahiers;
            })
        );
    }

    public getCahiersByHabilitation(idIntervenant: number, idOrganisme: number, codeStatut: string, codeHabilitation: string): Observable<Cahier[]> {
        let url = '';

        if (idOrganisme != undefined) {
            url = `&organisme=${idOrganisme}`;
        }

        return forkJoin([
            // eslint-disable-next-line max-len

            this.http.get<object[]>(
                `/api/declaration/private/habilitation/${codeHabilitation}/cahiers?intervenant=${idIntervenant}` + url + `&statutHabilitation=${codeStatut}`
            ),
            this.activitesService.getActivites() // activites must be loaded before deserialisation
        ]).pipe(
            map(([cahiers, activites]) => this.mapper.deserializeArray(cahiers, Cahier, { activites })),
            tap(cahiers => {
                CahiersService.cahiers = cahiers;
            })
        );
    }

    public getCahiersByProduits(idProduits: number[]): Observable<Cahier[]> {
        return forkJoin([
            this.http.get<object[]>(`${this.BASE_URL}/private/cahiers/produits/${idProduits}`),
            this.activitesService.getActivites() // activites must be loaded before deserialisation
        ]).pipe(
            // map(([cahiers]) => this.mapper.deserializeArray(cahiers, Cahier))
            map(([cahiers, activites]) => this.mapper.deserializeArray(cahiers, Cahier, { activites })),
            tap(cahiers => {
                CahiersService.cahiers = cahiers;
            })
        );
    }

    /**
     * Retrieve the cahier list base on organisme id
     * @param idOrganisme - Organisme id
     */
    public getCahiersByOrganisme(idOrganisme: number): Observable<Cahier[]> {
        return forkJoin([
            this.http.get<Cahier[]>(`/api/declaration/private/cahiers/organismes/${idOrganisme}`),
            this.activitesService.getActivites() // activites must be loaded before deserialisation
        ]).pipe(
            map(([cahiers, activites]) => this.mapper.deserializeArray(cahiers, Cahier, { activites })),
            tap(cahiers => {
                CahiersService.cahiers = cahiers;
            })
        );
    }

    /**
     * Récupérer tous les cahiers des charges dont l'opérateur a les habilitations
     * @param idOperateur - L'id de l'opérateur
     * @param idOrganisme - L'id de l'organisme (optionel)
     */
    getCahierHabilitesByOperateur(idOperateur: number, idOrganisme?: number): Observable<Cahier[]> {
        let url = `${this.BASE_URL}/private/operateurs/${idOperateur}/cahiers/habilites`;

        if (idOrganisme) {
            url += `?organisme=${idOrganisme}`;
        }

        return forkJoin([this.http.get<Cahier[]>(url), this.activitesService.getActivites()]).pipe(
            map(([cahiers, activites]) => this.mapper.deserializeArray(cahiers, Cahier, { activites })),
            tap(cahiers => {
                CahiersService.cahiers = cahiers;
            })
        );
    }
}
